import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Menubar from "../Menubar/Menubar";
import {
  Search,
  Download,
  SlidersHorizontal,
  BarChart3,
  HandCoins,
  PieChart,
  Mail,
  FileText,
  X,
} from "lucide-react";
import { FaEye, FaEdit } from "react-icons/fa";
import Loader from "../Loader/Loader";
import useMinimumLoading from "../hooks/Useminimumloading";
import { API_URL } from "../../Config";

const stats = [
  { label: "Total Revenue", value: "53,000", delta: "+55%", icon: BarChart3 },
  { label: "Pending Payments", value: "23", delta: "+5%", icon: HandCoins },
  {
    label: "Revenue by Property",
    value: "$173,000",
    delta: "+8%",
    icon: PieChart,
  },
];

// Backend dates come back as "YYYY-MM-DD" (or "YYYY-MM-DDTHH:mm:ss.sssZ") or null.
// Deliberately string-based rather than `new Date(...)` + local getters, so
// display can't be shifted by a day depending on the browser's timezone.
const formatDate = (value) => {
  if (!value) return "-";
  const datePart = String(value).split("T")[0];
  const [year, month, day] = datePart.split("-");
  if (!year || !month || !day) return value;
  return `${day}/${month}/${year}`;
};

const Bills = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("invoices");
  const [checkedRows, setCheckedRows] = useState({});
  const [allChecked, setAllChecked] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    const savedState = localStorage.getItem("menubarOpen");

    if (savedState !== null) {
      return JSON.parse(savedState);
    }

    return window.innerWidth >= 1024;
  });
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);

      if (mobile) {
        setIsSidebarOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  const [invoices, setInvoices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const showLoader = useMinimumLoading(isLoading);

  const [loadError, setLoadError] = useState(null);

  const [cashbackInvoices, setCashbackInvoices] = useState([]);
  const [isCashbackLoading, setIsCashbackLoading] = useState(true);
  const showLoaderCashback = useMinimumLoading(isCashbackLoading);

  const [cashbackLoadError, setCashbackLoadError] = useState(null);

  const [isDeleting, setIsDeleting] = useState(false);

  // Search
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const searchInputRef = useRef(null);

  // Selection is tab-specific (row index 0 could mean a different invoice on
  // each tab), so clear it whenever the tab changes to avoid stale selections.
  useEffect(() => {
    setCheckedRows({});
    setAllChecked(false);
    // Clear search when switching tabs so a filter from one tab doesn't
    // silently hide rows on the other.
    setSearchQuery("");
    setIsSearchOpen(false);
  }, [activeTab]);

  useEffect(() => {
    if (isSearchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isSearchOpen]);

  useEffect(() => {
    const fetchInvoices = async () => {
      try {
        setIsLoading(true);
        setLoadError(null);
        const res = await fetch(`${API_URL}/billing/invoices`);
        const data = await res.json();

        if (!res.ok || !data.success) {
          setLoadError(data.message || "Failed to load invoices.");
          return;
        }

        setInvoices(data.invoices || []);
      } catch (err) {
        console.error("Error loading invoices:", err);
        setLoadError(
          `Error: ${err.message || "Something went wrong while loading invoices."}`,
        );
      } finally {
        setIsLoading(false);
      }
    };

    if (activeTab === "invoices") {
      fetchInvoices();
    }
  }, [activeTab]);

  useEffect(() => {
    const fetchCashbackInvoices = async () => {
      try {
        setIsCashbackLoading(true);
        setCashbackLoadError(null);
        const res = await fetch(`${API_URL}/billing/cashback-invoices`);
        const data = await res.json();

        if (!res.ok || !data.success) {
          setCashbackLoadError(
            data.message || "Failed to load cashback invoices.",
          );
          return;
        }

        setCashbackInvoices(data.invoices || []);
      } catch (err) {
        console.error("Error loading cashback invoices:", err);
        setCashbackLoadError(
          "Something went wrong while loading cashback invoices.",
        );
      } finally {
        setIsCashbackLoading(false);
      }
    };

    if (activeTab === "cashback") {
      fetchCashbackInvoices();
    }
  }, [activeTab]);

  // Filtered rows for the active tab, matched against guest name and
  // invoice number (case-insensitive, trimmed). Falls back to the full
  // list when the query is empty.
  const filteredInvoices = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return invoices;
    return invoices.filter((row) => {
      const name = (row.guestname || "").toLowerCase();
      const invoiceno = (row.invoiceno || "").toLowerCase();
      return name.includes(q) || invoiceno.includes(q);
    });
  }, [invoices, searchQuery]);

  const filteredCashbackInvoices = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return cashbackInvoices;
    return cashbackInvoices.filter((row) => {
      const name = (row.guestname || "").toLowerCase();
      const invoiceno = (row.invoiceno || "").toLowerCase();
      return name.includes(q) || invoiceno.includes(q);
    });
  }, [cashbackInvoices, searchQuery]);

  const visibleRows =
    activeTab === "invoices"
      ? filteredInvoices
      : activeTab === "cashback"
        ? filteredCashbackInvoices
        : [];

  const toggleAll = () => {
    const next = !allChecked;
    setAllChecked(next);

    const map = {};
    visibleRows.forEach((_, i) => {
      map[i] = next;
    });

    setCheckedRows(map);
  };

  const toggleRow = (i) => {
    setCheckedRows((prev) => ({ ...prev, [i]: !prev[i] }));
  };

  const selectedCount = Object.values(checkedRows).filter(Boolean).length;

  const getSelectedIds = () => {
    return visibleRows
      .map((row, i) => {
        if (!checkedRows[i]) return null;
        return activeTab === "invoices" ? row.invoiceid : row.cashbackinvoiceid;
      })
      .filter((id) => id !== null);
  };

  const handleDeleteSelected = async () => {
    const ids = getSelectedIds();
    if (ids.length === 0) return;

    const label = activeTab === "invoices" ? "invoice" : "cashback invoice";
    const confirmed = window.confirm(
      `Are you sure you want to delete ${ids.length} selected ${label}${
        ids.length > 1 ? "s" : ""
      }? This cannot be undone.`,
    );
    if (!confirmed) return;

    const endpointSegment =
      activeTab === "invoices" ? "invoice" : "cashback-invoice";

    try {
      setIsDeleting(true);
      const results = await Promise.all(
        ids.map((id) =>
          fetch(`${API_URL}/billing/${endpointSegment}/${id}`, {
            method: "DELETE",
          })
            .then((res) => res.json())
            .catch(() => ({ success: false })),
        ),
      );

      const failedCount = results.filter((r) => !r.success).length;
      if (failedCount > 0) {
        alert(
          `${failedCount} of ${ids.length} could not be deleted. The rest were removed.`,
        );
      }

      if (activeTab === "invoices") {
        setInvoices((prev) =>
          prev.filter((row) => !ids.includes(row.invoiceid)),
        );
      } else {
        setCashbackInvoices((prev) =>
          prev.filter((row) => !ids.includes(row.cashbackinvoiceid)),
        );
      }

      setCheckedRows({});
      setAllChecked(false);
    } catch (err) {
      console.error("Error deleting selected rows:", err);
      alert("Something went wrong while deleting.");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white font-sans text-[#22261f]">
      <Menubar
        isOpen={isSidebarOpen}
        setIsOpen={setIsSidebarOpen}
        isMobile={isMobile}
      />

      {/* Page content — offset so it sits to the right of the fixed sidebar instead of underneath it */}
      <main
        className={`p-5 lg:p-6 transition-all duration-500 ${
          isMobile
            ? "w-full"
            : isSidebarOpen
              ? "ml-64 w-[calc(100%-16rem)]"
              : "ml-[68px] w-[calc(100%-68px)]"
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-[26px] font-medium text-[#2b2f27]">Accounting</h1>
          <div className="flex items-center gap-3 text-[#3a3f34]">
            {isSearchOpen && (
              <div className="flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-1.5">
                {/* <Search
                  size={16}
                  strokeWidth={1.75}
                  className="text-gray-400"
                /> */}
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={
                    activeTab === "invoices"
                      ? "Enter guest /invoice no."
                      : "Enter guest /invoice no."
                  }
                  className="text-sm outline-none focus:ring-0 focus:outline-none  border-none bg-transparent w-48 text-[#22261f] placeholder:text-gray-400"
                />
                <button
                  aria-label="Clear search"
                  className="hover:opacity-70"
                  onClick={() => {
                    setSearchQuery("");
                    setIsSearchOpen(false);
                  }}
                >
                  <X size={14} strokeWidth={2} />
                </button>
              </div>
            )}
            <button
              className="hover:opacity-70"
              aria-label="Search"
              onClick={() => setIsSearchOpen((prev) => !prev)}
            >
              <Search size={20} strokeWidth={1.75} />
            </button>
            {/* <button className="hover:opacity-70" aria-label="Download">
              <Download size={20} strokeWidth={1.75} />
            </button>
            <button className="hover:opacity-70" aria-label="Filter">
              <SlidersHorizontal size={20} strokeWidth={1.75} />
            </button> */}
          </div>
        </div>

        {/* Stat cards */}
        {/*  <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
          {stats.map(({ label, value, delta, icon: Icon }) => (
            <div
              key={label}
              className="relative rounded-lg bg-[#DAE0CE] px-6 py-5"
            >
              <div className="absolute top-5 right-5 rounded-md bg-[#22261f] text-white p-2.5">
                <Icon size={18} strokeWidth={1.75} />
              </div>
              <p className="text-[15px] text-[#4a4f42] mb-3">{label}</p>
              <p className="text-[26px] font-medium text-[#22261f]">
                {value}{" "}
                <span className="text-sm font-medium text-[#4f7a4a]">
                  {delta}
                </span>
              </p>
            </div>
          ))}
        </div>*/}

        {/* Tabs + Add Payment */}
        <div className="flex items-center justify-between mb-4">
          <div className="inline-flex border border-gray-300 rounded-lg overflow-hidden text-sm font-medium shadow-sm">
            <button
              onClick={() => setActiveTab("invoices")}
              className={`px-4 py-2 transition-colors duration-200 focus:outline-none ${
                activeTab === "invoices"
                  ? "bg-[#1D2826] text-white"
                  : "bg-white text-gray-500 hover:bg-green-50"
              }`}
            >
              Invoices
            </button>
            <button
              onClick={() => setActiveTab("cashback")}
              className={`px-4 py-2 transition-colors duration-200 focus:outline-none ${
                activeTab === "cashback"
                  ? "bg-[#1D2826] text-white"
                  : "bg-white text-gray-500 hover:bg-green-50"
              }`}
            >
              Cashback
            </button>
            {/* <button
              onClick={() => setActiveTab("proforma")}
              className={`px-4 py-2 transition-colors duration-200 focus:outline-none ${
                activeTab === "proforma"
                  ? "bg-[#1D2826] text-white"
                  : "bg-white text-gray-500 hover:bg-green-50"
              }`}
            >
              Proforma
            </button> */}
          </div>
          <div className="flex items-center gap-3">
            {activeTab !== "proforma" && selectedCount > 0 && (
              <button
                onClick={handleDeleteSelected}
                disabled={isDeleting}
                className={`rounded-lg border border-red-500 text-red-500 text-sm font-medium px-6 py-2.5 hover:opacity-90 ${
                  isDeleting ? "opacity-60 cursor-not-allowed" : ""
                }`}
              >
                {isDeleting ? "Deleting..." : `Delete (${selectedCount})`}
              </button>
            )}
            <button
              onClick={() => {
                if (activeTab === "invoices") {
                  navigate("/addinvoicebill");
                } else if (activeTab === "cashback") {
                  navigate("/addcashbackbill");
                }
                // Proforma is design-only for now. No route or backend action.
              }}
              className="rounded-lg bg-[#22261f] text-white text-sm font-medium px-6 py-2.5 hover:opacity-90"
            >
              + Add{" "}
              {activeTab === "invoices"
                ? "Invoice"
                : activeTab === "cashback"
                  ? "Cashback"
                  : "Proforma"}
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto rounded-xl border border-[#eceae0]">
          <table className="w-full text-sm">
            <thead className="text-xs text-gray-400 uppercase bg-green-50 border-b">
              <tr>
                <th className="py-3 px-4 font-medium w-10">
                  <input
                    type="checkbox"
                    checked={allChecked}
                    onChange={toggleAll}
                    className="accent-[#22261f]"
                  />
                </th>
                <th className="py-3 px-4 font-medium text-left">Name</th>
                <th className="py-3 px-4 font-medium">Invoice</th>
                <th className="py-3 px-4 font-medium">Check In</th>
                <th className="py-3 px-4 font-medium">Check Out</th>
                {/* <th className="py-3 px-4 font-medium">Status</th> */}
                <th className="py-3 px-4 font-medium">Payment</th>
                <th className="py-3 px-4 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {activeTab === "invoices" ? (
                showLoader ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="py-10 px-4 text-center text-[#8a8f7e]"
                    >
                      <Loader
                        label="Loading..."
                        fullScreen={false}
                        className="py-2"
                      />
                    </td>
                  </tr>
                ) : loadError ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="py-10 px-4 text-center text-red-600"
                    >
                      {loadError}
                    </td>
                  </tr>
                ) : invoices.length === 0 ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="py-10 px-4 text-center text-[#8a8f7e]"
                    >
                      No invoices added yet.
                    </td>
                  </tr>
                ) : filteredInvoices.length === 0 ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="py-10 px-4 text-center text-[#8a8f7e]"
                    >
                      No invoices match "{searchQuery}".
                    </td>
                  </tr>
                ) : (
                  filteredInvoices.map((row, i) => (
                    <tr
                      key={row.invoiceid}
                      className="border-t border-[#eceae0] hover:bg-[#fafaf6]"
                    >
                      <td className="py-3 px-4">
                        <input
                          type="checkbox"
                          checked={!!checkedRows[i]}
                          onChange={() => toggleRow(i)}
                          className="accent-[#22261f]"
                        />
                      </td>
                      <td className="py-3 px-4 font-medium text-[#22261f] text-left">
                        {row.guestname}
                      </td>
                      <td className="py-3 px-4 text-[#4a4f42] text-center">
                        {row.invoiceno}
                      </td>
                      <td className="py-3 px-4 text-[#4a4f42] text-center">
                        {formatDate(row.checkin)}
                      </td>
                      <td className="py-3 px-4 text-[#4a4f42] text-center">
                        {formatDate(row.checkout)}
                      </td>

                      <td className="py-3 px-4 text-[#22261f] text-center">
                        {row.total}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3 text-[#6b7062] justify-center">
                          <button
                            aria-label="Edit invoice"
                            className="hover:text-[#22261f]"
                            onClick={() =>
                              navigate(`/invoice/${row.invoiceid}/edit`)
                            }
                          >
                            <FaEdit size={15} />
                          </button>
                          <button
                            aria-label="View invoice"
                            className="hover:text-[#22261f]"
                            onClick={() =>
                              navigate(`/invoice-view/${row.invoiceid}`)
                            }
                          >
                            <FaEye size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )
              ) : activeTab === "cashback" ? (
                showLoaderCashback ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="py-10 px-4 text-center text-[#8a8f7e]"
                    >
                      <Loader
                        label="Loading..."
                        fullScreen={false}
                        className="py-2"
                      />
                    </td>
                  </tr>
                ) : cashbackLoadError ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="py-10 px-4 text-center text-red-600"
                    >
                      {cashbackLoadError}
                    </td>
                  </tr>
                ) : cashbackInvoices.length === 0 ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="py-10 px-4 text-center text-[#8a8f7e]"
                    >
                      No cashback entries to show yet.
                    </td>
                  </tr>
                ) : filteredCashbackInvoices.length === 0 ? (
                  <tr>
                    <td
                      colSpan={8}
                      className="py-10 px-4 text-center text-[#8a8f7e]"
                    >
                      No cashback entries match "{searchQuery}".
                    </td>
                  </tr>
                ) : (
                  filteredCashbackInvoices.map((row, i) => (
                    <tr
                      key={row.cashbackinvoiceid}
                      className="border-t border-[#eceae0] hover:bg-[#fafaf6]"
                    >
                      <td className="py-3 px-4">
                        <input
                          type="checkbox"
                          checked={!!checkedRows[i]}
                          onChange={() => toggleRow(i)}
                          className="accent-[#22261f]"
                        />
                      </td>
                      <td className="py-3 px-4 font-medium text-[#22261f] text-center">
                        {row.guestname}
                      </td>
                      <td className="py-3 px-4 text-[#4a4f42] text-center">
                        {row.invoiceno}
                      </td>
                      <td className="py-3 px-4 text-[#4a4f42] text-center">
                        {formatDate(row.checkin)}
                      </td>
                      <td className="py-3 px-4 text-[#4a4f42] text-center">
                        {formatDate(row.checkout)}
                      </td>

                      <td className="py-3 px-4 text-[#22261f] text-center">
                        {row.total}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3 text-[#6b7062] justify-center">
                          <button
                            aria-label="Edit cashback invoice"
                            className="hover:text-[#22261f]"
                            onClick={() =>
                              navigate(
                                `/cashback/${row.cashbackinvoiceid}/edit`,
                              )
                            }
                          >
                            <FaEdit size={15} />
                          </button>
                          <button
                            aria-label="View cashback invoice"
                            className="hover:text-[#22261f]"
                            onClick={() =>
                              navigate(
                                `/cashback-view/${row.cashbackinvoiceid}`,
                              )
                            }
                          >
                            <FaEye size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )
              ) : (
                <tr>
                  <td
                    colSpan={8}
                    className="py-10 px-4 text-center text-[#8a8f7e]"
                  >
                    No proforma entries to show yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
};

export default Bills;

// import React, { useState, useEffect, useMemo, useRef } from "react";
// import { useNavigate } from "react-router-dom";
// import Menubar from "../Menubar/Menubar";
// import {
//   Search,
//   Download,
//   SlidersHorizontal,
//   BarChart3,
//   HandCoins,
//   PieChart,
//   Mail,
//   FileText,
//   X,
// } from "lucide-react";
// import { FaEye, FaEdit } from "react-icons/fa";
// import Loader from "../Loader/Loader";
// import useMinimumLoading from "../hooks/Useminimumloading";
// import { API_URL } from "../../Config";

// const stats = [
//   { label: "Total Revenue", value: "53,000", delta: "+55%", icon: BarChart3 },
//   { label: "Pending Payments", value: "23", delta: "+5%", icon: HandCoins },
//   {
//     label: "Revenue by Property",
//     value: "$173,000",
//     delta: "+8%",
//     icon: PieChart,
//   },
// ];

// // Backend dates come back as "YYYY-MM-DD" (or "YYYY-MM-DDTHH:mm:ss.sssZ") or null.
// // Deliberately string-based rather than `new Date(...)` + local getters, so
// // display can't be shifted by a day depending on the browser's timezone.
// const formatDate = (value) => {
//   if (!value) return "-";
//   const datePart = String(value).split("T")[0];
//   const [year, month, day] = datePart.split("-");
//   if (!year || !month || !day) return value;
//   return `${day}/${month}/${year}`;
// };

// const Bills = () => {
//   const navigate = useNavigate();
//   const [activeTab, setActiveTab] = useState("invoices");
//   const [checkedRows, setCheckedRows] = useState({});
//   const [allChecked, setAllChecked] = useState(false);
//   const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
//   const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
//     const savedState = localStorage.getItem("menubarOpen");

//     if (savedState !== null) {
//       return JSON.parse(savedState);
//     }

//     return window.innerWidth >= 1024;
//   });
//   useEffect(() => {
//     const handleResize = () => {
//       const mobile = window.innerWidth < 1024;
//       setIsMobile(mobile);

//       if (mobile) {
//         setIsSidebarOpen(false);
//       }
//     };

//     window.addEventListener("resize", handleResize);
//     return () => window.removeEventListener("resize", handleResize);
//   }, []);
//   const [invoices, setInvoices] = useState([]);
//   const [isLoading, setIsLoading] = useState(true);
//   const showLoader = useMinimumLoading(isLoading);

//   const [loadError, setLoadError] = useState(null);

//   const [cashbackInvoices, setCashbackInvoices] = useState([]);
//   const [isCashbackLoading, setIsCashbackLoading] = useState(true);
//   const showLoaderCashback = useMinimumLoading(isCashbackLoading);

//   const [cashbackLoadError, setCashbackLoadError] = useState(null);

//   const [isDeleting, setIsDeleting] = useState(false);

//   // Search
//   const [isSearchOpen, setIsSearchOpen] = useState(false);
//   const [searchQuery, setSearchQuery] = useState("");
//   const searchInputRef = useRef(null);

//   // Selection is tab-specific (row index 0 could mean a different invoice on
//   // each tab), so clear it whenever the tab changes to avoid stale selections.
//   useEffect(() => {
//     setCheckedRows({});
//     setAllChecked(false);
//     // Clear search when switching tabs so a filter from one tab doesn't
//     // silently hide rows on the other.
//     setSearchQuery("");
//     setIsSearchOpen(false);
//   }, [activeTab]);

//   useEffect(() => {
//     if (isSearchOpen && searchInputRef.current) {
//       searchInputRef.current.focus();
//     }
//   }, [isSearchOpen]);

//   useEffect(() => {
//     const fetchInvoices = async () => {
//       try {
//         setIsLoading(true);
//         setLoadError(null);
//         const res = await fetch(`${API_URL}/billing/invoices`);
//         const data = await res.json();

//         if (!res.ok || !data.success) {
//           setLoadError(data.message || "Failed to load invoices.");
//           return;
//         }

//         setInvoices(data.invoices || []);
//       } catch (err) {
//         console.error("Error loading invoices:", err);
//         setLoadError(
//           `Error: ${err.message || "Something went wrong while loading invoices."}`,
//         );
//       } finally {
//         setIsLoading(false);
//       }
//     };

//     if (activeTab === "invoices") {
//       fetchInvoices();
//     }
//   }, [activeTab]);

//   useEffect(() => {
//     const fetchCashbackInvoices = async () => {
//       try {
//         setIsCashbackLoading(true);
//         setCashbackLoadError(null);
//         const res = await fetch(`${API_URL}/billing/cashback-invoices`);
//         const data = await res.json();

//         if (!res.ok || !data.success) {
//           setCashbackLoadError(
//             data.message || "Failed to load cashback invoices.",
//           );
//           return;
//         }

//         setCashbackInvoices(data.invoices || []);
//       } catch (err) {
//         console.error("Error loading cashback invoices:", err);
//         setCashbackLoadError(
//           "Something went wrong while loading cashback invoices.",
//         );
//       } finally {
//         setIsCashbackLoading(false);
//       }
//     };

//     if (activeTab === "cashback") {
//       fetchCashbackInvoices();
//     }
//   }, [activeTab]);

//   // Filtered rows for the active tab, matched against guest name and
//   // invoice number (case-insensitive, trimmed). Falls back to the full
//   // list when the query is empty.
//   const filteredInvoices = useMemo(() => {
//     const q = searchQuery.trim().toLowerCase();
//     if (!q) return invoices;
//     return invoices.filter((row) => {
//       const name = (row.guestname || "").toLowerCase();
//       const invoiceno = (row.invoiceno || "").toLowerCase();
//       return name.includes(q) || invoiceno.includes(q);
//     });
//   }, [invoices, searchQuery]);

//   const filteredCashbackInvoices = useMemo(() => {
//     const q = searchQuery.trim().toLowerCase();
//     if (!q) return cashbackInvoices;
//     return cashbackInvoices.filter((row) => {
//       const name = (row.guestname || "").toLowerCase();
//       const invoiceno = (row.invoiceno || "").toLowerCase();
//       return name.includes(q) || invoiceno.includes(q);
//     });
//   }, [cashbackInvoices, searchQuery]);

//   const visibleRows =
//     activeTab === "invoices" ? filteredInvoices : filteredCashbackInvoices;

//   const toggleAll = () => {
//     const next = !allChecked;
//     setAllChecked(next);

//     const map = {};
//     visibleRows.forEach((_, i) => {
//       map[i] = next;
//     });

//     setCheckedRows(map);
//   };

//   const toggleRow = (i) => {
//     setCheckedRows((prev) => ({ ...prev, [i]: !prev[i] }));
//   };

//   const selectedCount = Object.values(checkedRows).filter(Boolean).length;

//   const getSelectedIds = () => {
//     return visibleRows
//       .map((row, i) => {
//         if (!checkedRows[i]) return null;
//         return activeTab === "invoices" ? row.invoiceid : row.cashbackinvoiceid;
//       })
//       .filter((id) => id !== null);
//   };

//   const handleDeleteSelected = async () => {
//     const ids = getSelectedIds();
//     if (ids.length === 0) return;

//     const label = activeTab === "invoices" ? "invoice" : "cashback invoice";
//     const confirmed = window.confirm(
//       `Are you sure you want to delete ${ids.length} selected ${label}${
//         ids.length > 1 ? "s" : ""
//       }? This cannot be undone.`,
//     );
//     if (!confirmed) return;

//     const endpointSegment =
//       activeTab === "invoices" ? "invoice" : "cashback-invoice";

//     try {
//       setIsDeleting(true);
//       const results = await Promise.all(
//         ids.map((id) =>
//           fetch(`${API_URL}/billing/${endpointSegment}/${id}`, {
//             method: "DELETE",
//           })
//             .then((res) => res.json())
//             .catch(() => ({ success: false })),
//         ),
//       );

//       const failedCount = results.filter((r) => !r.success).length;
//       if (failedCount > 0) {
//         alert(
//           `${failedCount} of ${ids.length} could not be deleted. The rest were removed.`,
//         );
//       }

//       if (activeTab === "invoices") {
//         setInvoices((prev) =>
//           prev.filter((row) => !ids.includes(row.invoiceid)),
//         );
//       } else {
//         setCashbackInvoices((prev) =>
//           prev.filter((row) => !ids.includes(row.cashbackinvoiceid)),
//         );
//       }

//       setCheckedRows({});
//       setAllChecked(false);
//     } catch (err) {
//       console.error("Error deleting selected rows:", err);
//       alert("Something went wrong while deleting.");
//     } finally {
//       setIsDeleting(false);
//     }
//   };

//   return (
//     <div className="min-h-screen bg-white font-sans text-[#22261f]">
//       <Menubar
//         isOpen={isSidebarOpen}
//         setIsOpen={setIsSidebarOpen}
//         isMobile={isMobile}
//       />

//       {/* Page content — offset so it sits to the right of the fixed sidebar instead of underneath it */}
//       <main
//         className={`p-5 lg:p-6 transition-all duration-500 ${
//           isMobile
//             ? "w-full"
//             : isSidebarOpen
//               ? "ml-64 w-[calc(100%-16rem)]"
//               : "ml-[68px] w-[calc(100%-68px)]"
//         }`}
//       >
//         {/* Header */}
//         <div className="flex items-center justify-between mb-8">
//           <h1 className="text-[26px] font-medium text-[#2b2f27]">Accounting</h1>
//           <div className="flex items-center gap-3 text-[#3a3f34]">
//             {isSearchOpen && (
//               <div className="flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-1.5">
//                 {/* <Search
//                   size={16}
//                   strokeWidth={1.75}
//                   className="text-gray-400"
//                 /> */}
//                 <input
//                   ref={searchInputRef}
//                   type="text"
//                   value={searchQuery}
//                   onChange={(e) => setSearchQuery(e.target.value)}
//                   placeholder={
//                     activeTab === "invoices"
//                       ? "Enter guest /invoice no."
//                       : "Enter guest /invoice no."
//                   }
//                   className="text-sm outline-none focus:ring-0 focus:outline-none  border-none bg-transparent w-48 text-[#22261f] placeholder:text-gray-400"
//                 />
//                 <button
//                   aria-label="Clear search"
//                   className="hover:opacity-70"
//                   onClick={() => {
//                     setSearchQuery("");
//                     setIsSearchOpen(false);
//                   }}
//                 >
//                   <X size={14} strokeWidth={2} />
//                 </button>
//               </div>
//             )}
//             <button
//               className="hover:opacity-70"
//               aria-label="Search"
//               onClick={() => setIsSearchOpen((prev) => !prev)}
//             >
//               <Search size={20} strokeWidth={1.75} />
//             </button>
//             {/* <button className="hover:opacity-70" aria-label="Download">
//               <Download size={20} strokeWidth={1.75} />
//             </button>
//             <button className="hover:opacity-70" aria-label="Filter">
//               <SlidersHorizontal size={20} strokeWidth={1.75} />
//             </button> */}
//           </div>
//         </div>

//         {/* Stat cards */}
//         {/*  <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
//           {stats.map(({ label, value, delta, icon: Icon }) => (
//             <div
//               key={label}
//               className="relative rounded-lg bg-[#DAE0CE] px-6 py-5"
//             >
//               <div className="absolute top-5 right-5 rounded-md bg-[#22261f] text-white p-2.5">
//                 <Icon size={18} strokeWidth={1.75} />
//               </div>
//               <p className="text-[15px] text-[#4a4f42] mb-3">{label}</p>
//               <p className="text-[26px] font-medium text-[#22261f]">
//                 {value}{" "}
//                 <span className="text-sm font-medium text-[#4f7a4a]">
//                   {delta}
//                 </span>
//               </p>
//             </div>
//           ))}
//         </div>*/}

//         {/* Tabs + Add Payment */}
//         <div className="flex items-center justify-between mb-4">
//           <div className="inline-flex border border-gray-300 rounded-lg overflow-hidden text-sm font-medium shadow-sm">
//             <button
//               onClick={() => setActiveTab("invoices")}
//               className={`px-4 py-2 transition-colors duration-200 focus:outline-none ${
//                 activeTab === "invoices"
//                   ? "bg-[#1D2826] text-white"
//                   : "bg-white text-gray-500 hover:bg-green-50"
//               }`}
//             >
//               Invoices
//             </button>
//             <button
//               onClick={() => setActiveTab("cashback")}
//               className={`px-4 py-2 transition-colors duration-200 focus:outline-none ${
//                 activeTab === "cashback"
//                   ? "bg-[#1D2826] text-white"
//                   : "bg-white text-gray-500 hover:bg-green-50"
//               }`}
//             >
//               Cashback
//             </button>
//           </div>
//           <div className="flex items-center gap-3">
//             {selectedCount > 0 && (
//               <button
//                 onClick={handleDeleteSelected}
//                 disabled={isDeleting}
//                 className={`rounded-lg border border-red-500 text-red-500 text-sm font-medium px-6 py-2.5 hover:opacity-90 ${
//                   isDeleting ? "opacity-60 cursor-not-allowed" : ""
//                 }`}
//               >
//                 {isDeleting ? "Deleting..." : `Delete (${selectedCount})`}
//               </button>
//             )}
//             <button
//               onClick={() =>
//                 navigate(
//                   activeTab === "invoices"
//                     ? "/addinvoicebill"
//                     : "/addcashbackbill",
//                 )
//               }
//               className="rounded-lg bg-[#22261f] text-white text-sm font-medium px-6 py-2.5 hover:opacity-90"
//             >
//               + Add {activeTab === "invoices" ? "Invoice" : "Cashback"}
//             </button>
//           </div>
//         </div>

//         {/* Table */}
//         <div className="overflow-x-auto rounded-xl border border-[#eceae0]">
//           <table className="w-full text-sm">
//             <thead className="text-xs text-gray-400 uppercase bg-green-50 border-b">
//               <tr>
//                 <th className="py-3 px-4 font-medium w-10">
//                   <input
//                     type="checkbox"
//                     checked={allChecked}
//                     onChange={toggleAll}
//                     className="accent-[#22261f]"
//                   />
//                 </th>
//                 <th className="py-3 px-4 font-medium">Name</th>
//                 <th className="py-3 px-4 font-medium">Invoice</th>
//                 <th className="py-3 px-4 font-medium">Check In</th>
//                 <th className="py-3 px-4 font-medium">Check Out</th>
//                 {/* <th className="py-3 px-4 font-medium">Status</th> */}
//                 <th className="py-3 px-4 font-medium">Payment</th>
//                 <th className="py-3 px-4 font-medium">Actions</th>
//               </tr>
//             </thead>
//             <tbody>
//               {activeTab === "invoices" ? (
//                 showLoader ? (
//                   <tr>
//                     <td
//                       colSpan={8}
//                       className="py-10 px-4 text-center text-[#8a8f7e]"
//                     >
//                       <Loader
//                         label="Loading..."
//                         fullScreen={false}
//                         className="py-2"
//                       />
//                     </td>
//                   </tr>
//                 ) : loadError ? (
//                   <tr>
//                     <td
//                       colSpan={8}
//                       className="py-10 px-4 text-center text-red-600"
//                     >
//                       {loadError}
//                     </td>
//                   </tr>
//                 ) : invoices.length === 0 ? (
//                   <tr>
//                     <td
//                       colSpan={8}
//                       className="py-10 px-4 text-center text-[#8a8f7e]"
//                     >
//                       No invoices added yet.
//                     </td>
//                   </tr>
//                 ) : filteredInvoices.length === 0 ? (
//                   <tr>
//                     <td
//                       colSpan={8}
//                       className="py-10 px-4 text-center text-[#8a8f7e]"
//                     >
//                       No invoices match "{searchQuery}".
//                     </td>
//                   </tr>
//                 ) : (
//                   filteredInvoices.map((row, i) => (
//                     <tr
//                       key={row.invoiceid}
//                       className="border-t border-[#eceae0] hover:bg-[#fafaf6]"
//                     >
//                       <td className="py-3 px-4">
//                         <input
//                           type="checkbox"
//                           checked={!!checkedRows[i]}
//                           onChange={() => toggleRow(i)}
//                           className="accent-[#22261f]"
//                         />
//                       </td>
//                       <td className="py-3 px-4 font-medium text-[#22261f] text-center">
//                         {row.guestname}
//                       </td>
//                       <td className="py-3 px-4 text-[#4a4f42] text-center">
//                         {row.invoiceno}
//                       </td>
//                       <td className="py-3 px-4 text-[#4a4f42] text-center">
//                         {formatDate(row.checkin)}
//                       </td>
//                       <td className="py-3 px-4 text-[#4a4f42] text-center">
//                         {formatDate(row.checkout)}
//                       </td>

//                       <td className="py-3 px-4 text-[#22261f] text-center">
//                         {row.total}
//                       </td>
//                       <td className="py-3 px-4">
//                         <div className="flex items-center gap-3 text-[#6b7062] justify-center">
//                           <button
//                             aria-label="Edit invoice"
//                             className="hover:text-[#22261f]"
//                             onClick={() =>
//                               navigate(`/invoice/${row.invoiceid}/edit`)
//                             }
//                           >
//                             <FaEdit size={15} />
//                           </button>
//                           <button
//                             aria-label="View invoice"
//                             className="hover:text-[#22261f]"
//                             onClick={() =>
//                               navigate(`/invoice-view/${row.invoiceid}`)
//                             }
//                           >
//                             <FaEye size={15} />
//                           </button>
//                         </div>
//                       </td>
//                     </tr>
//                   ))
//                 )
//               ) : showLoaderCashback ? (
//                 <tr>
//                   <td
//                     colSpan={8}
//                     className="py-10 px-4 text-center text-[#8a8f7e]"
//                   >
//                     <Loader
//                       label="Loading..."
//                       fullScreen={false}
//                       className="py-2"
//                     />
//                   </td>
//                 </tr>
//               ) : cashbackLoadError ? (
//                 <tr>
//                   <td
//                     colSpan={8}
//                     className="py-10 px-4 text-center text-red-600"
//                   >
//                     {cashbackLoadError}
//                   </td>
//                 </tr>
//               ) : cashbackInvoices.length === 0 ? (
//                 <tr>
//                   <td
//                     colSpan={8}
//                     className="py-10 px-4 text-center text-[#8a8f7e]"
//                   >
//                     No cashback entries to show yet.
//                   </td>
//                 </tr>
//               ) : filteredCashbackInvoices.length === 0 ? (
//                 <tr>
//                   <td
//                     colSpan={8}
//                     className="py-10 px-4 text-center text-[#8a8f7e]"
//                   >
//                     No cashback entries match "{searchQuery}".
//                   </td>
//                 </tr>
//               ) : (
//                 filteredCashbackInvoices.map((row, i) => (
//                   <tr
//                     key={row.cashbackinvoiceid}
//                     className="border-t border-[#eceae0] hover:bg-[#fafaf6]"
//                   >
//                     <td className="py-3 px-4">
//                       <input
//                         type="checkbox"
//                         checked={!!checkedRows[i]}
//                         onChange={() => toggleRow(i)}
//                         className="accent-[#22261f]"
//                       />
//                     </td>
//                     <td className="py-3 px-4 font-medium text-[#22261f] text-center">
//                       {row.guestname}
//                     </td>
//                     <td className="py-3 px-4 text-[#4a4f42] text-center">
//                       {row.invoiceno}
//                     </td>
//                     <td className="py-3 px-4 text-[#4a4f42] text-center">
//                       {formatDate(row.checkin)}
//                     </td>
//                     <td className="py-3 px-4 text-[#4a4f42] text-center">
//                       {formatDate(row.checkout)}
//                     </td>

//                     <td className="py-3 px-4 text-[#22261f] text-center">
//                       {row.total}
//                     </td>
//                     <td className="py-3 px-4">
//                       <div className="flex items-center gap-3 text-[#6b7062] justify-center">
//                         <button
//                           aria-label="Edit cashback invoice"
//                           className="hover:text-[#22261f]"
//                           onClick={() =>
//                             navigate(`/cashback/${row.cashbackinvoiceid}/edit`)
//                           }
//                         >
//                           <FaEdit size={15} />
//                         </button>
//                         <button
//                           aria-label="View cashback invoice"
//                           className="hover:text-[#22261f]"
//                           onClick={() =>
//                             navigate(`/cashback-view/${row.cashbackinvoiceid}`)
//                           }
//                         >
//                           <FaEye size={15} />
//                         </button>
//                       </div>
//                     </td>
//                   </tr>
//                 ))
//               )}
//             </tbody>
//           </table>
//         </div>
//       </main>
//     </div>
//   );
// };

// export default Bills;
